<template>
  <div id="app">
    <el-container class="app-container">
      <!-- Header -->
      <el-header class="app-header">
        <div class="header-content">
          <div class="logo">
            <h1>🍽️ Almar Shop</h1>
          </div>
          <div class="user-info">
            <el-dropdown @command="handleUserCommand">
              <span class="user-dropdown">
                <el-avatar :size="32" :src="user?.picture" :icon="UserFilled" />
                <span class="user-name">{{ user?.name || 'User' }}</span>
                <el-icon><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">Profile</el-dropdown-item>
                  <el-dropdown-item command="logout" divided>Logout</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </el-header>

      <!-- Main Content -->
      <el-container>
        <!-- Sidebar -->
        <el-aside width="250px" class="app-sidebar">
          <el-menu
            :default-active="$route.path"
            class="sidebar-menu"
            router
            background-color="#f8f9fa"
            text-color="#2c3e50"
            active-text-color="#409eff"
          >
            <el-menu-item index="/dashboard">
              <el-icon><DataBoard /></el-icon>
              <span>Dashboard</span>
            </el-menu-item>
            
            <el-sub-menu index="transactions">
              <template #title>
                <el-icon><Document /></el-icon>
                <span>Transactions</span>
              </template>
              <el-menu-item index="/transactions">All Transactions</el-menu-item>
              <el-menu-item index="/transactions/new">Add Transaction</el-menu-item>
            </el-sub-menu>

            <el-sub-menu index="reports">
              <template #title>
                <el-icon><TrendCharts /></el-icon>
                <span>Reports</span>
              </template>
              <el-menu-item index="/reports/daily">Daily Reports</el-menu-item>
              <el-menu-item index="/reports/monthly">Monthly Reports</el-menu-item>
              <el-menu-item index="/reports/weekly">Weekly Reports</el-menu-item>
            </el-sub-menu>

            <el-menu-item index="/analytics">
              <el-icon><PieChart /></el-icon>
              <span>Analytics</span>
            </el-menu-item>
          </el-menu>
        </el-aside>

        <!-- Main Content Area -->
        <el-main class="app-main">
          <router-view />
        </el-main>
      </el-container>
    </el-container>

    <!-- Toast notifications -->
    <ToastContainer />
    
    <!-- Global Error Handler (invisible component) -->
    <GlobalErrorHandler />
  </div>
</template>

<script>
import { computed } from 'vue'
import { UserFilled, ArrowDown, DataBoard, Document, TrendCharts, PieChart } from '@element-plus/icons-vue'
import { ToastContainer } from 'vue3-toastify'
import GlobalErrorHandler from '@/components/GlobalErrorHandler.vue'

export default {
  name: 'App',
  components: {
    ToastContainer,
    GlobalErrorHandler
  },
  setup() {
    // Authentication removed - always show UI
    const isAuthenticated = computed(() => true)
    const user = computed(() => ({ name: 'User', picture: null }))

    const handleUserCommand = (command) => {
      if (command === 'logout') {
        // No authentication - just show message
        console.log('Logout clicked - no authentication required')
      } else if (command === 'profile') {
        // Handle profile navigation
        console.log('Profile clicked')
      }
    }

    return {
      isAuthenticated,
      user,
      handleUserCommand,
      UserFilled,
      ArrowDown,
      DataBoard,
      Document,
      TrendCharts,
      PieChart
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: #f5f7fa;
  color: #2c3e50;
}

#app {
  height: 100vh;
}

.app-container {
  height: 100vh;
}

.app-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.logo h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.user-dropdown:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-name {
  font-weight: 500;
}

.app-sidebar {
  background-color: #f8f9fa;
  border-right: 1px solid #e4e7ed;
}

.sidebar-menu {
  border-right: none;
  height: 100%;
}

.sidebar-menu .el-menu-item,
.sidebar-menu .el-sub-menu__title {
  height: 50px;
  line-height: 50px;
  font-weight: 500;
}

.sidebar-menu .el-menu-item:hover,
.sidebar-menu .el-sub-menu__title:hover {
  background-color: #e6f7ff;
}

.sidebar-menu .el-menu-item.is-active {
  background-color: #e6f7ff;
  color: #409eff;
  border-right: 3px solid #409eff;
}

.app-main {
  padding: 20px;
  background-color: #f5f7fa;
  overflow-y: auto;
}

/* Responsive design */
@media (max-width: 768px) {
  .app-sidebar {
    width: 200px !important;
  }
  
  .header-content {
    flex-direction: column;
    gap: 10px;
    padding: 10px 0;
  }
  
  .user-name {
    display: none;
  }
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style> 